import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import QuotesDeliveryColumn from "@/Shared/Quotes/CustomComponents/QuotesDeliveryColumn";
import QuotesPickupColumnForPickup from "@/Shared/Quotes/CustomComponents/QuotesPickupColumnForPickup";
import QuotesProgress from "@/Shared/Quotes/CustomComponents/QuotesProgress";
import QuotesTransitColumnForTracking from "@/Shared/Quotes/CustomComponents/QuotesTransitColumnForTracking";
import { describeHours } from "@/Helpers/describeHours";
import type { Broker } from "@freightsimple/generated-dashboard-openapi-client/models/Broker";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import type { TrackingLine } from "@freightsimple/generated-dashboard-openapi-client/models/TrackingLine";
import { ShipmentMostRecentTrackingLine } from "@/Shared/Shipment/CustomComponents/ShipmentMostRecentTrackingLine";
import ShipmentSection from "@/Shared/Shipment/CustomComponents/ShipmentSection";

// Like Quote object, but with fewer required fields
interface QuoteLike {
  carrierIdentifier: string;
  serviceDisplayName: string;
  transitBusinessDays?: number | undefined;
}

interface DetailsSectionForTrackingProps {
  pickupDate: moment.Moment;
  pickupHours: string | undefined;
  pickupLocation: Location | AddLocationLocationType;
  deliveryLocation: Location | AddLocationLocationType;
  pickupContact: Contact | undefined;
  deliveryContact: Contact | undefined;
  deliveryDeadline: string | undefined;
  appointmentDate: string | undefined;
  lineItems: Array<LineItem | AddLineItemType>;
  quote: QuoteLike; // Might not have all the info when rendering for tracking
  shipmentState: ShipmentState;
  actualDeliveryDate: string | undefined;
  actualDeliveryTime: string | undefined;
  proNumber: string | undefined;
  billOfLadingNumber?: string | undefined;
  expectedDeliveryDate: string | undefined;
  latestExpectedDeliveryDate: string | undefined;
  pickupReferenceNumber: string | undefined;
  deliveryReferenceNumber: string | undefined;
  deliveryAmazonReferenceId: string | undefined;
  deliveryAmazonShipmentId: string | undefined;
  notes: string | undefined;
  broker: Broker | undefined;
  mostRecentTrackingLine: TrackingLine | undefined;
  pickupBoothNumber: string | undefined;
  deliveryBoothNumber: string | undefined;
}
function ShipmentDetailsSectionForTracking(
  props: DetailsSectionForTrackingProps,
) {
  const {
    pickupDate,
    pickupLocation,
    pickupContact,
    pickupReferenceNumber,
    deliveryLocation,
    deliveryContact,
    deliveryDeadline,
    appointmentDate,
    lineItems,
    quote,
    shipmentState,
    actualDeliveryDate,
    actualDeliveryTime,
    expectedDeliveryDate,
    latestExpectedDeliveryDate,
    proNumber,
    billOfLadingNumber,
    deliveryReferenceNumber,
    deliveryAmazonReferenceId,
    deliveryAmazonShipmentId,
    notes,
    broker,
    mostRecentTrackingLine,
    pickupBoothNumber,
    deliveryBoothNumber,
  } = props;

  const deliveryHours = describeHours(deliveryLocation.hours);

  return (
    <ShipmentSection title="Details" id="details-section">
      <ShipmentMostRecentTrackingLine
        mostRecentTrackingLine={mostRecentTrackingLine}
      />
      <QuotesProgress shipmentState={shipmentState} />
      <div className="h-[16px]" />
      <div className="flex w-full flex-row items-start">
        <QuotesPickupColumnForPickup
          lineItems={lineItems}
          pickupDate={pickupDate}
          pickupLocation={pickupLocation}
          pickupContact={pickupContact}
          pickupReferenceNumber={pickupReferenceNumber}
          pickupBoothNumber={pickupBoothNumber}
          shipmentState={shipmentState}
        />
        <QuotesTransitColumnForTracking
          quote={quote}
          shipmentState={shipmentState}
          proNumber={proNumber}
          billOfLadingNumber={billOfLadingNumber}
          notes={notes}
          broker={broker}
        />
        <QuotesDeliveryColumn
          quote={quote}
          deliveryHours={deliveryHours}
          appointmentDate={appointmentDate}
          deliveryLocation={deliveryLocation}
          deliveryContact={deliveryContact}
          deliveryDeadline={deliveryDeadline}
          shipmentState={shipmentState}
          actualDeliveryDate={actualDeliveryDate}
          actualDeliveryTime={actualDeliveryTime}
          expectedDeliveryDate={expectedDeliveryDate}
          deliveryReferenceNumber={deliveryReferenceNumber}
          deliveryAmazonReferenceId={deliveryAmazonReferenceId}
          deliveryAmazonShipmentId={deliveryAmazonShipmentId}
          deliveryBoothNumber={deliveryBoothNumber}
          latestExpectedDeliveryDate={latestExpectedDeliveryDate}
          showNotesSection={true}
        />
      </div>
    </ShipmentSection>
  );
}
export default ShipmentDetailsSectionForTracking;
